import { mergeClassNames } from "../../utils/classes";
import Icon from "../Icon/Icon";
import { LoadingSpinner } from "../Loading/LoadingSpinner";
import {
	getAlignSelfClassName,
	getButtonDisabledClassName,
	getIconAlignClassName,
	getIconSize,
	getLoadingSpinnerAppearance,
	getMarginClassName,
} from "./button.helper";

import type { FontelloIcons } from "../../assets/fontello";
import type {
	Appearance,
	CommonButtonProps,
	ExternalLinkButtonProps,
	IconAlign,
	LinkButtonProps,
	NormalButtonProps,
	SubmitButtonProps,
} from "./button.types";

// prettier-ignore
import * as styles from "./button.module.scss";
// prettier-ignore
import * as baseStyles from "./button.base.module.scss";

import { isValidElement } from "react";

import type { ComponentType, JSX, ReactNode, Ref, RefObject } from "react";

interface ButtonIconProps extends CommonButtonProps {
	icon: FontelloIcons | JSX.Element;
	children?: (string | number) | (string | number)[];
}

interface ButtonTextProps extends CommonButtonProps {
	icon?: FontelloIcons | JSX.Element;
	children?: (string | number) | (string | number)[];
}

export type ButtonProps = (ButtonIconProps | ButtonTextProps) & {
	appearance?: Appearance;
	/** @deprecated Icon should only be aligned to the left. */
	iconAlign?: IconAlign;
	stretch?: boolean;
	/** @deprecated Should only be used for design system internal overrides. */
	className?: string;
};

export type ButtonPropsUnion = ButtonProps &
	(NormalButtonProps | LinkButtonProps | ExternalLinkButtonProps | SubmitButtonProps) & {
		ref?: Ref<HTMLButtonElement | HTMLAnchorElement>;
	};

export type ButtonLinkComponentType = ComponentType<{
	id?: string | undefined;
	title?: string | undefined;
	className?: string | undefined;
	"data-testid"?: string | undefined;
	children: ReactNode;
	to: string;
	onClick?: (e: React.MouseEvent) => void | undefined;
	ref?: React.Ref<HTMLAnchorElement> | undefined;
}>;

let LinkComponent: ButtonLinkComponentType | null = null;

export const initializeButtonLinkComponent = (linkComponentImplementation: ButtonLinkComponentType) => {
	LinkComponent = linkComponentImplementation;
};

export const Button = ({
	ref,
	children,
	icon,
	disabled,
	loading,
	title,
	testId,
	id,
	autoFocus,
	appearance = "primary",
	size = "normal",
	iconAlign = "left",
	stretch,
	align,
	margin,
	className,
	...props
}: ButtonPropsUnion) => {
	const isDisabled = disabled || loading;

	const showIconLeft = iconAlign === "left";
	const showIconRight = iconAlign !== "left";

	const classNames = mergeClassNames(
		baseStyles.button,
		styles.button,
		baseStyles[appearance],
		baseStyles[size],
		stretch === true && styles.stretch,
		stretch === false && styles.autoResize,
		loading && baseStyles.loading,
		disabled && baseStyles.disabled,
		disabled && getButtonDisabledClassName(appearance),
		align && getAlignSelfClassName(align),
		margin && getMarginClassName(margin),
		className,
	);

	const iconClassNames = mergeClassNames(
		baseStyles.icon,
		getIconSize(size),
		getIconAlignClassName(iconAlign),
		loading && styles.hide,
	);

	const commonProps = {
		id,
		title: title ?? (typeof children === "string" || typeof children === "number" ? children.toString() : undefined),
		className: classNames,
		"data-testid": testId,
	};

	const renderIcon = () =>
		isValidElement(icon) ? (
			<span className={iconClassNames}>{icon}</span>
		) : (
			<Icon className={iconClassNames} name={icon as FontelloIcons} size={getIconSize(size)} />
		);

	const renderSpinnerWhenLoading = () => {
		if (!loading) {
			return null;
		}

		return (
			<span className={styles.loadingSpinner}>
				<LoadingSpinner appearance={getLoadingSpinnerAppearance(appearance)} size="small" />
			</span>
		);
	};

	const inner = (
		<>
			{icon && showIconLeft && renderIcon()}
			<span className={loading ? styles.hide : undefined}>{children ?? <wbr />}</span>
			{icon && showIconRight && renderIcon()}
		</>
	);

	if (props.type === "link" && disabled) {
		return (
			<a {...commonProps} ref={ref as RefObject<HTMLAnchorElement>}>
				{inner}
			</a>
		);
	}

	if (props.type === "link" && !props.external) {
		if (!LinkComponent) {
			throw new Error(
				"LinkComponent is not initialized. Please call initializeButtonLinkComponent in your application to set it up.",
			);
		}

		return (
			<LinkComponent
				{...commonProps}
				onClick={(e: React.MouseEvent) => (disabled ? e.preventDefault() : props.onNavigation?.())}
				to={props.route}
				ref={ref as RefObject<HTMLAnchorElement>}
			>
				{inner}
			</LinkComponent>
		);
	}

	if (props.type === "link" && props.external) {
		return (
			<a
				{...commonProps}
				onClick={e => (disabled ? e.preventDefault() : props.onNavigation?.())}
				href={props.route}
				download={props.download}
				target={props.target}
				ref={ref as RefObject<HTMLAnchorElement>}
			>
				{inner}
			</a>
		);
	}

	if (props.type === "submit") {
		return (
			<button
				{...commonProps}
				disabled={isDisabled}
				onClick={isDisabled ? undefined : props.onClick}
				autoFocus={autoFocus}
				type="submit"
				ref={ref as RefObject<HTMLButtonElement>}
			>
				{renderSpinnerWhenLoading()}
				{inner}
			</button>
		);
	}

	return (
		<button
			{...commonProps}
			onClick={isDisabled ? undefined : props.onClick}
			disabled={isDisabled}
			autoFocus={autoFocus}
			type="button"
			ref={ref as RefObject<HTMLButtonElement>}
		>
			{renderSpinnerWhenLoading()}
			{inner}
		</button>
	);
};
